import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import NavLink from '../common/NavLink';
import { getIdToken } from '../Firebase';
import { slugify } from '../../utils/slugify';

const API_HEADERS = {
  'Content-Type': 'application/json',
  'X-API-Key': process.env.REACT_APP_MAKE_API_KEY,
};

const hasGroup = (group) => {
  const value = (group || '').toString().trim();
  return value && value.toLowerCase() !== 'null' && value !== 'Brak';
};

const leagueLabel = (row) => (
  hasGroup(row.leagueGroup)
    ? `${row.competitionName} (${row.leagueGroup.trim()})`
    : row.competitionName
);

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('pl-PL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const PastMatchesWithoutResults = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getIdToken(false);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/past-matches-without-results`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const fetchCompetitionData = async (row) => {
    setActionLoadingId(`/api/scrap-data-${row.competitionId}`);
    setError('');
    setMessage('');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/scrap-data`,
        { competitionId: row.competitionId },
        { headers: API_HEADERS }
      );
      setMessage(response.data?.message || `Pobrano dane dla ${leagueLabel(row)}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message);
    } finally {
      setActionLoadingId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 1 }}>Ładowanie...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1360, mx: 'auto', px: { xs: 1, sm: 2 }, py: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
            Mecze przeszłe bez wyniku
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Zaplanowane mecze aktywnego sezonu, których termin minął ponad 3 godziny temu, ale nadal nie mają wyniku.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={load} disabled={loading || Boolean(actionLoadingId)}>
          Odśwież
        </Button>
      </Stack>

      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Chip size="small" label={`Liczba meczów: ${rows.length}`} />
      </Stack>

      <Paper sx={{ overflow: 'auto', bgcolor: '#1e1e1e' }}>
        <Table size="small" sx={{ minWidth: 1120 }}>
          <TableHead>
            <TableRow>
              <TableCell>Mecz</TableCell>
              <TableCell>Termin</TableCell>
              <TableCell>Liga</TableCell>
              <TableCell>Info</TableCell>
              <TableCell>Źródło</TableCell>
              <TableCell align="right">Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Brak przeszłych meczów bez wyniku.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.map((row) => {
              const homeUrl = `/team/${row.homeTeamId}/${slugify(row.homeTeamName)}`;
              const awayUrl = `/team/${row.awayTeamId}/${slugify(row.awayTeamName)}`;
              const competitionUrl = `/liga/${row.competitionId}/${slugify(leagueLabel(row))}`;
              const fetchLoading = actionLoadingId === `/api/scrap-data-${row.competitionId}`;

              return (
                <TableRow hover key={row.matchId}>
                  <TableCell>
                    <Stack spacing={0.35}>
                      <Typography component="span" sx={{ fontWeight: 700 }}>
                        <Typography component={NavLink} to={homeUrl} sx={{ color: '#fff', fontWeight: 700 }}>
                          {row.homeTeamName}
                        </Typography>
                        {' vs '}
                        <Typography component={NavLink} to={awayUrl} sx={{ color: '#fff', fontWeight: 700 }}>
                          {row.awayTeamName}
                        </Typography>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        match_id: {row.matchId}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{formatDate(row.matchDate)}</TableCell>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <Typography
                        component={NavLink}
                        to={competitionUrl}
                        sx={{ color: '#90caf9', fontWeight: 700 }}
                      >
                        {leagueLabel(row)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        competition_id: {row.competitionId}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={row.additionalInfo ? 'text.primary' : 'text.secondary'}>
                      {row.additionalInfo || 'brak'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.link ? (
                      <MuiLink href={row.link} target="_blank" rel="noreferrer">
                        90minut
                      </MuiLink>
                    ) : (
                      <Typography variant="body2" color="text.secondary">brak</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => fetchCompetitionData(row)}
                      disabled={Boolean(actionLoadingId)}
                    >
                      {fetchLoading ? <CircularProgress size={16} /> : 'Pobierz dane'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default PastMatchesWithoutResults;
